<template>
    <div v-if="balise.blocageDemarreur" class="tag-control mb-3">
        <div class="tag-control-title">
            Fonction blocage démarreur
        </div>

        <div class="tag-control-row">
            <div class="tag-control-meta">
                <span class="tag-control-label">
                    Fonctionnalité
                </span>

                <span
                    class="tag-control-state"
                    :class="{
                        'tag-control-state--enabled': isEnabled,
                        'tag-control-state--disabled': !isEnabled
                    }"
                >
                    {{ isEnabled ? 'Activée' : 'Désactivée' }}
                </span>
            </div>

            <div class="tag-control-actions">
                <div
                    v-if="actionLoading"
                    class="tag-control-loading"
                >
                    <span
                        class="spinner-border spinner-border-sm"
                        aria-hidden="true"
                    ></span>
                </div>

                <div
                    v-else
                    class="form-check form-switch tag-control-switch"
                >
                    <input
                        :id="switchId"
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        :checked="isEnabled"
                        :disabled="showDisableConfirmation"
                        @change="onSwitchChange"
                    >

                    <label class="visually-hidden" :for="switchId">
                        Activer ou désactiver la fonctionnalité de blocage
                        démarreur par TAG
                    </label>
                </div>
            </div>
        </div>

        <p class="tag-control-description mb-0">
            Empêche le démarrage du véhicule lorsqu’aucun TAG autorisé
            n’est détecté.
        </p>

        <div
            v-if="showDisableConfirmation"
            class="tag-control-confirmation"
        >
            <div class="tag-control-confirmation-content">
                <i class="fa-solid fa-triangle-exclamation"></i>

                <div>
                    <div class="tag-control-confirmation-title">
                        Désactiver le blocage démarreur par TAG ?
                    </div>

                    <p class="tag-control-confirmation-text mb-0">
                        Le véhicule pourra démarrer sans TAG autorisé.
                        La fonctionnalité restera désactivée jusqu’à sa
                        réactivation manuelle.
                    </p>
                </div>
            </div>

            <div class="tag-control-confirmation-actions">
                <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="actionLoading"
                    @click="cancelDisable"
                >
                    Annuler
                </button>

                <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    :disabled="actionLoading"
                    @click="confirmDisable"
                >
                    <span
                        v-if="actionLoading"
                        class="spinner-border spinner-border-sm me-1"
                        aria-hidden="true"
                    ></span>

                    Désactiver
                </button>
            </div>
        </div>

        <div
            v-else-if="!isEnabled"
            class="tag-control-warning"
        >
            <i class="fa-solid fa-triangle-exclamation"></i>

            <span>
                Fonctionnalité désactivée : le véhicule peut démarrer sans TAG.
                Elle devra être réactivée manuellement.
            </span>
        </div>
    </div>
</template>

<script>
import CmdService from '@/services/cmd.service'

const COMMAND_DISABLE_STARTER_BLOCKING = 146;
const COMMAND_ENABLE_STARTER_BLOCKING = 147;

export default {
    name: 'TagControl',

    props: {
        balise: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            actionLoading: false,
            showDisableConfirmation: false,
            loadingTimer: null
        };
    },

    computed: {
        isEnabled() {
            return (
                this.balise?.blocageDemarreurEnabled === true ||
                this.balise?.blocageDemarreurEnabled === 1
            );
        },

        switchId() {
            return `tag-control-switch-${this.balise.id}`;
        }
    },

    beforeUnmount() {
        if (this.loadingTimer) {
            clearTimeout(this.loadingTimer);
        }
    },

    methods: {
        onSwitchChange(event) {
            const requestedEnabled = event.target.checked;

            // Le switch reste basé sur l’état réellement reçu de la balise.
            event.target.checked = this.isEnabled;

            if (this.actionLoading) {
                return;
            }

            if (requestedEnabled) {
                this.sendCommand(COMMAND_ENABLE_STARTER_BLOCKING);
                return;
            }

            this.showDisableConfirmation = true;
        },

        cancelDisable() {
            if (this.actionLoading) {
                return;
            }

            this.showDisableConfirmation = false;
        },

        async confirmDisable() {
            await this.sendCommand(COMMAND_DISABLE_STARTER_BLOCKING);
        },

        async sendCommand(commandId) {
            if (!this.balise?.id || this.actionLoading) {
                return;
            }

            this.actionLoading = true;

            if (this.loadingTimer) {
                clearTimeout(this.loadingTimer);
            }

            this.loadingTimer = setTimeout(() => {
                this.actionLoading = false;
                this.loadingTimer = null;
            }, 5000);

            try {
                await CmdService.runRegistered(this.balise.id, commandId);
                this.showDisableConfirmation = false;
            } catch (error) {
                console.error(
                    'Erreur lors de l’envoi de la commande de protection par TAG',
                    error
                );
            }
        }
    }
};
</script>

<style scoped>
.tag-control {
    display: grid;
    gap: .25rem;
}

.tag-control-title {
    font-size: .76rem;
    font-weight: 700;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-top: .4rem;
}

.tag-control-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .6rem;
    padding: .25rem .1rem;
    border-bottom: 1px dashed #f1f3f5;
    flex-wrap: wrap;
}

.tag-control-meta {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .45rem;
    min-width: 0;
    flex: 1 1 12rem;
}

.tag-control-label {
    font-size: .82rem;
    font-weight: 600;
    color: #495057;
    white-space: normal;
}

.tag-control-state {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 4.6rem;
    padding: .12rem .45rem;
    border-radius: 999px;
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: .03em;
    border: 1px solid transparent;
    white-space: nowrap;
}

.tag-control-state--enabled {
    color: #146c43;
    background-color: #d1e7dd;
}

.tag-control-state--disabled {
    color: #842029;
    background-color: #f8d7da;
}

.tag-control-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 2.5rem;
    flex: 0 0 auto;
}

.tag-control-loading {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    min-height: 1.3rem;
}

.tag-control-switch {
    flex: 0 0 auto;
    margin: 0;
    padding-left: 2.8rem;
}

.tag-control-switch .form-check-input {
    width: 2.5rem;
    height: 1.3rem;
    margin-left: -2.8rem;
    cursor: pointer;
}

.tag-control-switch .form-check-input:disabled {
    cursor: not-allowed;
}

.tag-control-description {
    padding: .1rem .1rem .35rem;
    font-size: .72rem;
    line-height: 1.35;
    color: #6c757d;
}

.tag-control-confirmation {
    display: grid;
    gap: .65rem;
    padding: .6rem .1rem 0;
    border-top: 1px solid #f1aeb5;
}

.tag-control-confirmation-content {
    display: flex;
    align-items: flex-start;
    gap: .55rem;
    color: #842029;
}

.tag-control-confirmation-content > i {
    flex: 0 0 auto;
    margin-top: .15rem;
}

.tag-control-confirmation-title {
    margin-bottom: .2rem;
    font-size: .8rem;
    font-weight: 700;
}

.tag-control-confirmation-text {
    font-size: .74rem;
    line-height: 1.4;
}

.tag-control-confirmation-actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: .4rem;
}

.tag-control-warning {
    display: flex;
    align-items: flex-start;
    gap: .5rem;
    padding: .5rem .1rem 0;
    border-top: 1px solid #ffecb5;
    color: #856404;
    font-size: .74rem;
    line-height: 1.4;
}

.tag-control-warning > i {
    flex: 0 0 auto;
    margin-top: .15rem;
}

@media (max-width: 420px) {
    .tag-control-confirmation-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
    }
}
</style>